import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEye, FaEdit, FaSearch, FaCalendar, FaUser, FaDollarSign, FaTruck } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.ADMIN.ORDERS, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/admin/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(t('admin.errorUpdatingOrderStatus'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border border-amber-200';
      case 'processing': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'shipped': return 'bg-purple-100 text-purple-800 border border-purple-200';
      case 'delivered': return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border border-red-200';
      default: return 'bg-cigar-charcoal text-white border border-cigar-charcoal';
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !selectedStatus || order.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const statuses = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cigar-cream to-cigar-silver flex items-center justify-center">
        <div className="text-2xl text-cigar-dark font-cigar">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cigar-cream to-cigar-silver py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-cigar font-bold text-cigar-dark mb-2">{t('admin.ordersManagement')}</h1>
          <p className="text-cigar-charcoal text-lg">{t('admin.trackManageOrders')}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-blue-500 border border-cigar-silver">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <FaCalendar className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-cigar-charcoal">{t('admin.totalOrders')}</p>
                <p className="text-2xl font-semibold text-cigar-dark">{orders.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-amber-500 border border-cigar-silver">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-100 text-amber-600">
                <FaTruck className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-cigar-charcoal">{t('admin.pending')}</p>
                <p className="text-2xl font-semibold text-cigar-dark">
                  {orders.filter(o => o.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-emerald-500 border border-cigar-silver">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-emerald-100 text-emerald-600">
                <FaDollarSign className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-cigar-charcoal">{t('admin.revenue')}</p>
                <p className="text-2xl font-semibold text-cigar-dark">
                  ${orders.reduce((sum, order) => sum + (order.totalPrice || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-purple-500 border border-cigar-silver">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <FaUser className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-cigar-charcoal">{t('admin.customers')}</p>
                <p className="text-2xl font-semibold text-cigar-dark">
                  {new Set(orders.map(o => o.user?._id)).size}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-xl p-6 mb-8 border border-cigar-silver">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-cigar-charcoal" />
              <input
                type="text"
                placeholder={t('admin.searchOrders')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
              />
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
            >
              <option value="">{t('admin.allStatuses')}</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedStatus('');
              }}
              className="w-full px-4 py-2 border border-cigar-silver text-cigar-charcoal font-medium rounded-lg hover:bg-cigar-cream transition-all duration-200"
            >
              {t('admin.clearFilters')}
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-cigar-silver">
          <div className="px-6 py-4 border-b border-cigar-silver bg-gradient-to-r from-cigar-cream to-cigar-silver">
            <h3 className="text-lg font-semibold text-cigar-dark">
              {t('admin.orders', { count: filteredOrders.length })}
            </h3>
          </div>
          
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <FaCalendar className="w-16 h-16 text-cigar-charcoal mx-auto mb-4" />
              <h3 className="text-lg font-medium text-cigar-charcoal mb-2">{t('admin.noOrdersFound')}</h3>
              <p className="text-cigar-charcoal">{t('admin.adjustSearchCriteria')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-cigar-silver">
                <thead className="bg-gradient-to-r from-cigar-cream to-cigar-silver">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.orderId')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.customer')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.items')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.total')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-cigar-dark uppercase tracking-wider">
                      {t('admin.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-cigar-silver">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-cigar-cream transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-cigar-dark">
                        #{order._id.slice(-6)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-cigar-dark">
                            {order.user?.name || t('na')}
                          </div>
                          <div className="text-sm text-cigar-charcoal">
                            {order.user?.email || t('na')}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-cigar-dark">
                        {order.items?.length || 0} {t('admin.items')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-cigar-bronze">
                        ${order.totalPrice || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={order.status || 'pending'}
                          onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status || 'pending')} focus:outline-none focus:ring-2 focus:ring-cigar-bronze`}
                        >
                          {statuses.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-cigar-dark">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="text-cigar-bronze hover:text-cigar-dark mr-3 p-2 hover:bg-cigar-cream rounded-lg transition-all duration-200"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="text-cigar-bronze hover:text-cigar-dark p-2 hover:bg-cigar-cream rounded-lg transition-all duration-200"
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-cigar-silver">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-cigar-dark">
                  {t('admin.orderDetails', { id: selectedOrder._id.slice(-6) })}
                </h2>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="text-cigar-charcoal hover:text-cigar-dark p-2 hover:bg-cigar-cream rounded-lg transition-all duration-200"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-cigar-dark mb-2">{t('admin.customerInformation')}</h3>
                  <div className="bg-gradient-to-r from-cigar-cream to-cigar-silver p-3 rounded-lg border border-cigar-silver">
                    <p><strong className="text-cigar-dark">{t('admin.name')}:</strong> <span className="text-cigar-charcoal">{selectedOrder.user?.name || t('na')}</span></p>
                    <p><strong className="text-cigar-dark">{t('admin.email')}:</strong> <span className="text-cigar-charcoal">{selectedOrder.user?.email || t('na')}</span></p>
                    <p><strong className="text-cigar-dark">{t('admin.phone')}:</strong> <span className="text-cigar-charcoal">{selectedOrder.user?.phone || t('na')}</span></p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-cigar-dark mb-2">{t('admin.orderItems')}</h3>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-r from-cigar-cream to-cigar-silver p-3 rounded-lg border border-cigar-silver flex justify-between">
                        <span className="text-cigar-charcoal">{item.product?.name || t('productNameNA')}</span>
                        <span className="text-cigar-bronze font-medium">
                          {t('admin.qty')}: {item.quantity} × ${item.product?.price || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-cigar-silver pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-cigar-dark">{t('admin.total')}:</span>
                    <span className="text-xl font-bold text-cigar-bronze">
                      ${selectedOrder.totalPrice || 0}
                    </span>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowOrderModal(false)}
                    className="flex-1 bg-gradient-to-r from-cigar-brown to-cigar-light hover:from-cigar-dark hover:to-cigar-brown text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {t('admin.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
